/* File holds all the neccessary constants apart from any configuration constants 
NOTE: Don't put anything configuration related here , put them under /config/.. and use it them using --useConfiguration-- hook
*/
/* importing images */
import EMRRXLogo from "@assets/images/rx_logo.png";
import BookingConfirmationScreen from "@assets/images/screen_booking_confirmation.svg";
import CustomerOnboardingScreen from "@assets/images/screen_customer_onboarding.svg";
import CustomerSearchScreen from "@assets/images/screen_customer_search.svg";
import PWLScreen from "@assets/images/screen_pwl.jpg";
import SlotsScreen from "@assets/images/screen_slots.jpg";
import VeticCharWhiteLogo from "@assets/images/vetic_new.svg";
import VeticLogo from "@assets/images/veticLogo.svg";
import NoAddress from "@assets/no_records/no_address.svg";
import NoAppointmentsFound from "@assets/no_records/no_appointments.jpg";
import CartEmpty from "@assets/no_records/no_cart.svg";
import NoRecordFoundOverbooking from "@assets/no_records/no_permission_overbooking.svg";

import NoRecordPWL from "@assets/no_records/no_pwl_results.svg";

import { differenceInHours, fromUnixTime } from "date-fns";

import { ISelectMenuItemData } from "./app/components/ui/interface";
import { CUSTOMER_DETAILS_TABS } from "./app/routes";

const generateTimeItems = (length: number, interval: number = 5) => {
  return Array(length)
    .fill(0)
    .map((_v, count) => {
      const value = count * interval;
      const formattedValue = value < 10 ? `0${value}` : `${value}`;
      return { id: `${count}`, title: formattedValue } as ISelectMenuItemData;
    });
};

/* Helper Functions Ends Here */
export interface IVMSConstants {
  string: VMSStringConstantsType;
  mapped: VMSMappedObjectConstantsType;
  array: VMSArrayConstantType;
  images: VMSImagesConstantTypes;
  theme: VMSThemeConstantsType;
}

export type VMSStringConstantsType = {
  [key in
  | "product_title"
  | "dogSpecieTypeId"
  | "catSpecieTypeId"
  | "product_description"
  | "dev"
  | "prod"
  | "stage"
  | "viewDOBFormat"
  | "fieldDOBFormat"
  | "baseRedirectURL"
  | "invalidAccessURL"
  | "slotStatusAvailable"
  | "slotStatusBooked"
  | "slotStatusOverbooking"
  | "currentDate"
  | "customerToggleCode"
  | "petToggleCode"
  | "mobileCountryCode"
  | "dogSpecieTypeName"
  | "catSpecieTypeName"
  | "customerMemberText"
  | "customerNonMemberText"
  | "VMSSourceId"
  | "appointmentStatusWaiting"
  | "appointmentStatusAttending"
  | "appointmentStatusUpcoming"
  | "appointmentStatusCompleted"
  | "appointmentStatusCancelled"
  | "appointmentStatusNoShow"
  | "lateBookingNormal"
  | "lateBookingApplied"
  | "bookingTypeNormal"
  | "bookingTypeOverbooking"
  | "bookingTypeLate"
  | "whatsappAddressRequestSuccessMessage"]: string;
};

export type VMSThemeConstantsType = {
  buttons: {
    baseThemes: {
      solid: string;
      secondary: string;
      soft: string;
      primary: string;
      success: string;
      "check-in": string;
    };
    sizes: {
      SM: string;
      MD: string;
      LG: string;
      XL: string;
      "2XL": string;
      PWL: string;
    };
  };
};

export type VMSKeyedRecord<T> = {
  [key: string | number]: T;
};

export type VMSMappedObjectConstantsType = {
  [key in
    | "stats"
    | "vmsPlaceholders"
    | "bookingStatusesColors"
    | "bookingHistoryColors"
    | "petStatusColors"
    | "appointmentNotesTags"
    | "appointmentStatusMapping"
    | "appointmentBookingCommentEditStatusMapping"
    | "appointmentButtonMapping"
    | "appointmentActionDescriptions"
    | "rotaManagementConstant"
    | "POStatusBadgeColor"
    | "EMRSectionColors"
    | "bookingStatuses"
    | "petStatusBackgroundColors"]: {
    [key: string | number]: any;
  };
};

export type VMSArrayConstantType = {
  [key in
    | "excludedNavs"
    | "RBACExcludedNavs"
    | "petGenders"
    | "pet_species"
    | "address_labels"
    | "serviceList"
    | "petMoods"
    | "pet_statuses"
    | "dayClosingShift"
    | "DAY_CLOSURE_TAB"
    | "giantBreeds"
    | "dogBreeds"
    | "catBreeds"
    | "navExcludedNavs"
    | "excludedCustomerProfileTabs"
    | "overBookingStartTimeItems"
    | "overBookingStartTimeMinuteItems"
    | "customerProfileSearchToggles"
    | "searchCustomerToggleCommandPallete"
    | "dashboardCardItems"
    | "appointmentCancelationReasons"
    | "rescheduleAndCancelAllowedFor"
    | "rescheduleAndCancelAllowedCBLFor"
    | "bookAgainAllowedCBLFor"
    | "customerProfileHeaderTabs"
    | "PWLActionButtonAllowedFor"
    | "indianStates"
    | "DentalScoreListIteam"
    | "BOOKING_COMPLETE_REMARKS"
    | "PO_CREATION_PERMISION"
    | "PO_INVOICE_UPLOAD_PERMISION"
    | "OVERDUE_REASONS"
    | "BodyScoreListIteam"
    | "EMRTemplateType"
    | "serviceMinuteStartTimeMinuteItems"]: Array<any>;
};

export type VMSImagesConstantTypes = {
  [key in
  | "BASIC"
  | "NO_RECORD_OVERBOOKING_PERMISSION"
  | "NO_APPOINTMENTS"
  | "NO_PWL_RESULTS"
  | "NO_ADDRESS"
  | "VETIC_LOGO"
  | "VETIC_CHAR_LOGO_WHITE"
  | "EMR_RX_LOGO"
  | "SCREEN_PWL"
  | "SCREEN_CUSTOMER_SEARCH"
  | "SCREEN_CUSTOMER_ONBOARDING"
  | "SCREEN_SLOTS"
  | "SCREEN_APPOINTMENT"
  | "NO_CART"]: string;
};

export const VMSStringConstants: VMSStringConstantsType = {
  currentDate: new Date(Date.now()).toISOString(),
  appointmentStatusWaiting: "waiting",
  bookingTypeNormal: "normal_booking",
  bookingTypeOverbooking: "over_booking",
  bookingTypeLate: "late_booking",
  whatsappAddressRequestSuccessMessage:
    "Message sent to Customer on WhatsApp, Kindly ask customer to fill the address. You can check the address shared by clicking on 'Select Address' again.",
  lateBookingNormal: "0",
  lateBookingApplied: "1",
  appointmentStatusAttending: "attending",
  appointmentStatusUpcoming: "upcoming",
  appointmentStatusCompleted: "completed",
  appointmentStatusCancelled: "cancel",
  appointmentStatusNoShow: "no show",
  product_title: "Vetic VMS",
  mobileCountryCode: "+91",
  product_description: "Vetic VMS to serve pets and the vet companies even better",
  // ENV Modes
  viewDOBFormat: "dd-MM-yyyy",
  fieldDOBFormat: "yyyy-MM-dd",
  dev: "development",
  prod: "production",
  stage: "staging",
  baseRedirectURL: "/app/dashboard",
  invalidAccessURL: "/app/invalid-access",
  slotStatusAvailable: "0",
  slotStatusBooked: "1",
  slotStatusOverbooking: "2",
  customerToggleCode: "0",
  petToggleCode: "1",
  dogSpecieTypeId: "1",
  catSpecieTypeId: "2",
  dogSpecieTypeName: "canine",
  catSpecieTypeName: "feline",
  customerMemberText: "member",
  customerNonMemberText: "non member",
  VMSSourceId: "5",
};

export const VMSArrayConstants: VMSArrayConstantType = {
  EMRTemplateType: [
    { id: "OPD Consultation", title: "OPD Consultation" },
    { id: "Post Grooming Consultation", title: "Post Grooming Consultation" },
  ],
  DAY_CLOSURE_TAB: [
    {
      id: "p0",
      name: "Summary",
      type: "Summary",
      priority: 1,
      approvalsRequired: false,
      description: "Ensure appointment attains terminal status (Completed / No show / Cancelled).",
      status: "red",
    },
    {
      id: "p1",
      name: "Appointment Status",
      type: "appointment",
      priority: 1,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p2",
      name: "Payments & Billing",
      type: "appointment",
      priority: 1,
      approvalsRequired: false,
      description: "Ensure appointment attains terminal status (Completed / No show / Cancelled).",
      status: "red",
    },
    {
      id: "p3",
      name: "Cash Management",
      type: "appointment",
      priority: 1,
      approvalsRequired: false,
      description: "Job cards and surgery/sterilization documents must be closed for billed/completed appts.",
      status: "green",
    },

    {
      id: "p4",
      name: "Upload Documents",
      type: "Financial",
      priority: 0,
      approvalsRequired: false,
      status: "red",
    },
    {
      id: "p5",
      name: "Internal Lab Reports",
      type: "operational",
      priority: 1,
      approvalsRequired: false,
      description: "All internal lab tests should have reports uploaded.",
      status: "red",
    },
    {
      id: "p6",
      name: "External Lab Reports",
      type: "operational",
      priority: 1,
      approvalsRequired: false,
      description: "All external lab tests should have reports uploaded.",
      status: "red",
    },
    {
      id: "p7",
      name: "Receiving",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p8",
      name: "Pending GRN",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p9",
      name: "Pending Delivery",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p10",
      name: "Pending Returns",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p11",
      name: "Bad Inventory Actions",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p12",
      name: "Service Invoices",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p13",
      name: "Video Consultation",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p14",
      name: "Open Carts",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
    {
      id: "p15",
      name: "Staff Attendance",
      type: "operational",
      priority: 2,
      approvalsRequired: false,
      status: "green",
    },
  ],
  BOOKING_COMPLETE_REMARKS: [
    { title: "Service Not Done", id: "Service Not Done" },
    { title: "Service stopped", id: "Service stopped" },
    { title: "Service Denied", id: "Service Denied" },
    { title: "Partial service given", id: "Partial service given" },
    { title: "Duplicate booking ID", id: "Duplicate booking ID" },
    { title: "Checkin by mistake", id: "Checkin by mistake" },
  ],
  OVERDUE_REASONS: [
    { title: "Hygiene Pad Not Used", id: "Hygiene Pad Not Used" },
    { title: "Deworming Administration Not Done", id: "Deworming Administration Not Done" },
    { title: "IPD Patient Overdue", id: "IPD Patient Overdue" },
    { title: "Doctor Committed Discount", id: "Doctor Committed Discount" },
    { title: "Surgery Discount not showing in VMS", id: "Surgery Discount not showing in VMS" },
    { title: "BD Committed Discount - but not visible in VMS", id: "BD Committed Discount - but not visible in VMS" },
    {
      title: "Payment Deducted from Client but Failed in POS/VMS",
      id: "Payment Deducted from Client but Failed in POS/VMS",
    },
    { title: "Vendor Overdue", id: "Vendor Overdue" },
    { title: "AHS Overdue", id: "AHS Overdue" },
    { title: "Incorrect Billing Done by Clinic", id: "Incorrect Billing Done by Clinic" },
    { title: "CM/ACM Missed to Collect Payment", id: "CM/ACM Missed to Collect Payment" },
    { title: "Pet Injury or Death During Service", id: "Pet Injury or Death During Service" },
    { title: "Bad Debt Client - Needs to be blocked in VMS", id: "Bad Debt Client - Needs to be blocked in VMS" },
    { title: "DW ONLY Dr. Pandey approved discount", id: "DW ONLY Dr. Pandey approved discount" },
  ],
  DentalScoreListIteam: [
    {
      title: "0",
      id: 0,
    },
    {
      title: "1",
      id: 1,
    },
    {
      title: "2",
      id: 2,
    },
    {
      title: "3",
      id: 3,
    },
    {
      title: "4",
      id: 4,
    },
  ],
  BodyScoreListIteam: [
    {
      title: "1",
      id: 1,
    },
    {
      title: "2",
      id: 2,
    },
    {
      title: "3",
      id: 3,
    },
    {
      title: "4",
      id: 4,
    },
    {
      title: "5",
      id: 5,
    },
  ],
  pet_statuses: [
    {
      id: "1",
      status: "Active",
    },
    {
      id: "2",
      status: "InActive",
    },
    {
      id: "3",
      status: "Deceased",
    },
  ],
  petMoods: [
    {
      id: "1",
      title: "Aggressive",
    },
    {
      id: "2",
      title: "Not Friendly",
    },
    {
      id: "3",
      title: "Very Friendly",
    },
    {
      id: "4",
      title: "Friendly",
    },
    {
      id: "5",
      title: "Nervous",
    },
    {
      id: "6",
      title: "Unpredictable",
    },
  ],

  pet_species: [
    {
      id: "1",
      title: "Dog",
    },
    {
      id: "2",
      title: "Cat",
    },
    {
      id: "3",
      title: "Other",
    },
    {
      id: "15",
      title: "Giant Breed",
    },
  ],
  serviceList: [
    {
      label: "AHS",
      service_id: "1f610a8d-9350-44bb-aa82-5002212a4f32",
      subservice_id: "",
    },
    //  subservice_id: "4b6c1202-e96f-4d35-a3e3-21464fa0229e",
    // },
    {
      label: "OPD",
      service_id: "2fa0dc20-2385-11ed-8ca0-1e00da26097a",
      subservice_id: "d10441ac-4549-11ed-af80-d1c5db9bbce2",
    },

    {
      label: "HC",
      service_id: "d10441ad-4549-11ed-af80-d1c5db9bbce2",
      subservice_id: "d10441ad-4549-11ed-af80-d1c5db9bbce5",
    },
    {
      label: "Bath",
      service_id: "d10441ad-4549-11ed-af80-d1c5db9bbce2",
      subservice_id: "d10441ad-4549-11ed-af80-d1c5db9bbce3",
    },
    {
      label: "HC & Bath",
      service_id: "d10441ad-4549-11ed-af80-d1c5db9bbce2",
      subservice_id: "d10441ad-4549-11ed-af80-d1c5db9bbce4",
    },
    {
      label: "Vaccination",
      service_id: "d83fdcb0-66a6-4079-adb4-4d15ec984b44",
      subservice_id: "85b05a0d-fd02-4999-ba23-dd0d36b609df",
    },
  ],
  address_labels: [
    {
      label: "1",
      value: "Home",
    },
    {
      label: "2",
      value: "Work",
    },
    {
      label: "3",
      value: "Others",
    },
  ],
  petGenders: [
    {
      id: "1",
      title: "Male",
    },
    {
      id: "2",
      title: "Female",
    },
    {
      id: "3",
      title: "Other",
    },
  ],
  navExcludedNavs: ["CUSTOMER_PROFILE"],
  excludedCustomerProfileTabs: ["OTHER_INFORMATION_TAB", "ACTIVITY_TAB"],
  customerProfileHeaderTabs: [
    {
      title: "other_information",
      description: "Other information about the customer",
      url: CUSTOMER_DETAILS_TABS.OTHER_INFORMATION_TAB.url,
      isActive: false,
      id: "OTHER_INFORMATION_TAB",
    },
    {
      title: "appointments",
      description: "Appointments for the customer",
      url: CUSTOMER_DETAILS_TABS.APPOINTMENTS_TAB.url,
      isActive: false,
      id: "APPOINTMENTS_TAB",
    },
    {
      title: "EMR",
      description: "EMR description about the customer",
      url: CUSTOMER_DETAILS_TABS.EMR_TAB.url,
      id: "EMR_TAB",
      isActive: false,
    },
    {
      title: "vaccination",
      description: "Vaccination details about the pets registered with the customer",
      url: CUSTOMER_DETAILS_TABS.VACCINATION_TAB.url,
      id: "VACCINATION_TAB",
      isActive: false,
    },
    // {
    //   title: "Medicine History",
    //   description: "Vaccination details about the pets registered with the customer",
    //   url: CUSTOMER_DETAILS_TABS.MEDICINE_HISTORY.url,
    //   id: "MEDICINE_HISTORY",
    //   isActive: false,
    // },
    {
      title: "lab_test",
      description: "Lab Test details about the customer",
      url: CUSTOMER_DETAILS_TABS.LAB_TEST_TAB.url,
      id: "LAB_TEST_TAB",
      isActive: false,
    },
    {
      title: "lab_interpretation",
      description: "Lab report interpretations of the customer's pet",
      url: CUSTOMER_DETAILS_TABS.LAB_INTERPRETATION_TAB.url,
      id: "LAB_INTERPRETATION_TAB",
      isActive: false,
    },

    {
      title: "activity",
      description: "Activities breif about the customer",
      url: CUSTOMER_DETAILS_TABS.ACTIVITY_TAB.url,
      id: "ACTIVITY_TAB",
      isActive: false,
    },
    {
      title: "invoice",
      description: "Invoice details about the customer",
      url: CUSTOMER_DETAILS_TABS.INVOICE_TAB.url,
      id: "INVOICE_TAB",
      isActive: false,
    },

    {
      title: "membership details",
      description: "Membership details of a customer",
      url: CUSTOMER_DETAILS_TABS.MEMBERSHIP_DETAILS_TAB.url,
      id: "MEMBERSHIP_DETAILS_TAB",
      isActive: false,
    },
    {
      title: "Reminders",
      description: "Lab Test details about the customer",
      url: CUSTOMER_DETAILS_TABS.REMINDERS_TAB.url,
      id: "REMINDERS_TAB",
      isActive: false,
    },
    {
      title: "documents",
      description: "Certificates associated with the customer",
      url: `${CUSTOMER_DETAILS_TABS.CERTIFICATES_TAB.url}`,
      id: "CERTIFICATES_TAB",
      isActive: false,
    },
    {
      title: "Forms",
      description: "clinic form for a customer",
      url: CUSTOMER_DETAILS_TABS.FORM_TAB.url,
      id: "FORM_TAB",
      isActive: false,
    },
    {
      title: "Service Desk",
      description: "ticket details about the customer",
      url: CUSTOMER_DETAILS_TABS.TICKECT_TAB.url,
      id: "TICKECT_TAB",
      isActive: false,
    },
  ],
  indianStates: [
    { id: 1, title: "Delhi" },
    { id: 2, title: "Haryana" },
    { id: 3, title: "Karnataka" },
    { id: 4, title: "Maharashtra" },
    { id: 5, title: "Telangana" },
    { id: 6, title: "Uttar Pradesh" },
    { id: 7, title: "Gujarat" },
    { id: 8, title: "Arunachal Pradesh" },
    { id: 9, title: "Himachal Pradesh" },
    { id: 10, title: "Jharkhand" },
    { id: 11, title: "Assam" },
    { id: 12, title: "Kerala" },
    { id: 13, title: "Madhya Pradesh" },
    { id: 14, title: "Bihar" },
    { id: 15, title: "Manipur" },
    { id: 16, title: "Meghalaya" },
    { id: 17, title: "Mizoram" },
    { id: 18, title: "Nagaland" },
    { id: 19, title: "Odisha" },
    { id: 20, title: "Punjab" },
    { id: 21, title: "Rajasthan" },
    { id: 22, title: "Sikkim" },
    { id: 23, title: "Tamil Nadu" },
    { id: 24, title: "Chhattisgarh" },
    { id: 25, title: "Tripura" },
    { id: 26, title: "Goa" },
    { id: 27, title: "Uttarakhand" },
    { id: 28, title: "West Bengal" },
    { id: 29, title: "Andaman and Nicobar Islands" },
    { id: 30, title: "Chandigarh" },
    { id: 31, title: "Dadra and Nagar Haveli and Daman and Diu" },
    { id: 32, title: "Andhra Pradesh" },
    { id: 33, title: "Lakshadweep" },
    { id: 34, title: "Puducherry" },
  ],

  appointmentCancelationReasons: [
    { id: "6", title: "Price issue, customer asked to cancel." },
    {
      id: "1",
      title: "Would get late - informed before appointment time.",
    },
    { id: "2", title: "Pet deceased / died." },
    { id: "3", title: "Went out of town." },
    { id: "4", title: "Pet has recovered - no need for visit." },
    { id: "8", title: "Asked to cancel - No reason given." },
    { id: "9", title: "Client asking for Tele-consult instead." },
    { id: "10", title: "Weather not favourable, will visit later." },
    { id: "11", title: "DNP, cancelled after No Show." },
    { id: "12", title: "Went to competition." },
    { id: "13", title: "Went to other Vetic clinic." },
    {
      id: "14",
      title: "Running late > 15 mins, next slot not available to serve.",
    },
    {
      id: "15",
      title: "Booked as a test/understand booking process on App.",
    },
    {
      id: "16",
      title: "Asking for grooming service change, respective slot not available.",
    },
    {
      id: "17",
      title: "Already visited, no need for today's appointment.",
    },
    {
      id: "18",
      title: "Changed Appt to other Vetic clinic / Went to other Vetic clinic.",
    },
    { id: "188", title: "Client asking for Home Visit instead." },
    { id: "19", title: "To change the Pet in booking." },
    { id: "20", title: "To change the Service Type in booking." },
    { id: "21", title: "specific Doctor/Groomer not available." },
    { id: "22", title: "Unhappy, not going to visit." },
    { id: "22", title: "Duplicate profile exists." },
    { id: "22", title: "Slots not available for multipets." },
    { id: "22", title: "Pet Parent not well." },
    { id: "22", title: "PP wants to stay in grooming area." },
    { id: "22", title: "Unable to reschedule." },
    { id: "721", title: "Other" },
  ],
  rescheduleAndCancelAllowedFor: ["waiting", "upcoming", "no show"],
  rescheduleAndCancelAllowedCBLFor: ["waiting", "upcoming", "no show"],
  bookAgainAllowedCBLFor: ["completed", "attending", "faild", "cancelled"],
  excludedNavs: [
    "DASHBOARD",
    "CLINIC_LAB_REPORT",
    "BOM_TAX_DETAILS",
    "BOM_GENERAL_DETAILS",
    "SLOTS",
    "VOUCHER_SETUP",
    "GIFT_SETUP",
    "INVALID_ACCESS",
    "INVALID_LOGIN",
    "OVERBOOKING",
    "HAIRCUT_TO_BATH",
    "DETAILS_CUSTOMER",
    "CONFIGURATION",
    "CART_MANAGEMENT",
    "TEST",
    "QUICK_COMMERCE",
    "ROTA",
    "CUSTOMER_SUPPORT",
    "CUSTOMER_EMR",
    "EMR_PDF_VIEW",
    "EMR_CREATION",
    "VMS_SETUP",
    "HSN_AND_TAX_SETUP",
    "TAX_ADD",
    "BATCH_SETUP",
    "BATCH_ADD",
    "MANUFACTURER_SETUP",
    "MANUFACTURER_ADD",
    "VENDORS_LIST",
    "PRODUCT_LIST",
    "PRODUCT_ADD",
    "SERVICE_LIST",
    "SERVICE_ADD",
    "QC_TRANSFER_ORDER",
    "VENDOR_ADD",
    "PURCHASE_ADD",
    "GRN_DONE",
    "PENDING_GOODS",
    "TRANSFER_GOOD_INVENTORY",
    "TRANSFER_BAD_INVENTORY",
    "TRANSFER_ORDER_READ_ONLY",
    "TRANSFER_ORDER_DETAILS",
    "STOCK_INCOMPLETE_FORM",
    "BOM_ONBOARD",
    "BOM_SETUP",
    "SERVICE_DESK",
    "COUPON_SERVICE",
    "MEMBERSHIP_SETUP",
    "MEMBERSHIP_EXTENSION",
    "ITEM_GROUP_SETUP",
    "ITEM_GROUP_ONBOARD",
    "WHITELIST_MOBILE",
    "CALL_ANALYSIS",
    "CATEGORY_SETUP",
    "CATEGORY_ONBOARD",
    "SUB_CATEGORY_SETUP",
    "SUB_CATEGORY_ONBOARD",
    "CATEGORY_MAPPING",
    "CATEGORY_MAPPING_ONBOARD",
    "MAPPED_CLINIC_AND_CITY",
    "INTERNAL_LAB_REPORT",
    "EXTERNAL_LAB_REPORT",
    "DATA_MAPPINGS",
    "AHS_CREATE_INVENTORY_TRANSFER",
    "TILES_PAGE",
  ],
  RBACExcludedNavs: ["INVALID_ACCESS", "INVALID_LOGIN", "VMS_SETUP", "DASHBOARD", "DOCTOR_INVENTORY","MEMBERSHIP_EXTENSION"],

  overBookingStartTimeItems: generateTimeItems(24, 1),
  overBookingStartTimeMinuteItems: generateTimeItems(12, 5),
  serviceMinuteStartTimeMinuteItems: generateTimeItems(12, 5),
  customerProfileSearchToggles: [
    { id: VMSStringConstants.customerToggleCode, title: "Customer" },
    { id: VMSStringConstants.petToggleCode, title: "Pets" },
  ],
  dashboardCardItems: [
    {
      id: "add_customer",
      title: "Add Customer",
      description: "Add a customer with pet to continue booking flow.",
      icon: "LuUserPlus2",
      background: "bg-pink-500",
      url: "/app/customer/profile/add",
    },
    {
      id: "pwl",
      title: "Patient Waiting List",
      description: "List of patients for bookings.",
      icon: "TbClockHeart",
      background: "bg-yellow-500",
      url: "/app/patient-waiting-list",
    },
    {
      id: "cao",
      title: "Create Allow Overbooking",
      description: "Add staff members so that they can use and create overbookings.",
      icon: "SiStackoverflow",
      background: "bg-blue-500",
      url: "/app/appointment/overbooking",
    },
    {
      id: "cab",
      title: "Create A Booking",
      description: "Create a booking for a customer and respective pet.",
      icon: "MdAddchart",
      background: "bg-indigo-500",
      url: "/app/customer/profile/search",
    },
    {
      id: "sfc",
      title: "Search For Customer",
      description: "Search for customer",
      icon: "MdOutlineScreenSearchDesktop",
      background: "bg-green-500",
      url: "/app/customer/profile/search",
    },
    {
      id: "sfp",
      title: "Search For Pet",
      description: "Search for a pet.",
      icon: "PiDog",
      background: "bg-purple-500",
      url: "/app/customer/profile/search",
    },
  ],
  searchCustomerToggleCommandPallete: [
    {
      title: "Customers",
      toggleCode: VMSStringConstants.customerToggleCode,
      description: "Search in the customers using their Mobile, Name or provided Customer Code",
      current: true,
    },
    {
      title: "Pets",
      toggleCode: VMSStringConstants.petToggleCode,
      description: "Search in the pets either with Pet Code or Name",
      current: false,
    },
  ],
};

export const VMSMappedObjectConstants: VMSMappedObjectConstantsType = {
  appointmentActionDescriptions: {
    waiting: "Are you sure you want to start this appointment?",
    attending: "Are you sure you want to complete this appointment?",
    upcoming: "Are you sure you want to check-in for this appointment?",
    "no show": "Are you sure you want to check-in for this appointment?",
    completed: "",
    cancelled: "",
  },
  POStatusBadgeColor: {
    pre_draft:
      "inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10",
    draft:
      "inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10",
    pending_invoice_upload:
      "inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20",
    pending_invoice_verification:
      "inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10",
    awaiting_grn:
      "inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10",
    sent_to_finance:
      "inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20",
    issue_in_invoice_upload:
      "inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10",
    issue_in_grn:
      "inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10",
    closed:
      "inline-flex items-center rounded-md bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700 ring-1 ring-inset ring-pink-700/10",
  },
  appointmentButtonMapping: {
    waiting: "Start Visit",
    attending: "Complete",
    upcoming: "Check-in",
    "no show": "Check-in",
    completed: "",
    cancelled: "",
  },
  appointmentStatusMapping: {
    waiting: "attending",
    attending: "check_out",
    upcoming: "waiting",
    "no show": "waiting",
    completed: "",
    cancelled: "",
  },
  appointmentBookingCommentEditStatusMapping: {
    upcoming: "Pending",
    waiting: "Waiting",
    attending: "attending",
  },
  appointmentNotesTags: {
    reason: "Reason For Appointment",
    coupon: "Discount Coupon",
    alternativeCoupon: "Alternative Service Discount",
    membershipName: "Membership Pitched to Client",
    vaccination: "Client will bring Vaccination Diary",
    hygienePackage: "Hygiene package add-on required",
    otherComment: "Other",
  },
  petStatusColors: {
    active: "text-green-700 ",
    inactive: "text-blue-700 ",
    deceased: "text-red-700 ",
  },
  petStatusBackgroundColors: {
    active: "bg-green-700",
    inactive: " bg-blue-700",
    deceased: "bg-red-700",
  },
  bookingStatusesColors: {
    cancelled: "bg-[#FF7593] text-black",
    completed: "bg-[#33B081] text-black",
    upcoming: "bg-[#FFF0A1] text-black ",
    "check out": "bg-gray-100 text-black",
    "no show": "bg-[#F7C6D1] text-black",
    attending: "bg-[#E0F5ED] text-black",
    waiting: "bg-[#FFF2E2] text-black ",
    "on hold": "bg-[#FFF0A1] text-black ",
    failed: "bg-red-400 text-black ",
    failed_confirmed_later: "bg-red-400 text-black",
  },
  bookingHistoryColors: {
    cancel: "bg-[#FF7593] text-black",
    completed: "bg-[#33B081] text-black",
    pending: "bg-[#FFF0A1] text-black ",
    "check out": "bg-gray-100 text-black",
    "no show": "bg-[#F7C6D1] text-black",
    attending: "bg-[#E0F5ED] text-black",
    waiting: "bg-[#FFF2E2] text-black ",
    "on hold": "bg-[#FFF0A1] text-black ",
    failed: "bg-red-400 text-black ",
  },
  stats: {
    0: "Total",
    1: "Upcoming",
    2: "Waiting",
    3: "Attending",
    4: "Completed",
    5: "Cancelled",
  },
  vmsPlaceholders: {
    commandPalleteSearchByCustomerPlaceholder: "Search customer with ID, Mobile or Name.",
    commandPalleteSearchByPetPlaceholder: "Search pets with ID , Name or Customer Mobile ",
  },
  rotaManagementConstant: {
    standardShift: "Standard Shifts",
    activeShift: "Active Shifts",
    inActiveShift: "Inactive Shifts",
    addNewShift: "+ Add New Shift",
  },
  bookingStatuses: [
    {
      id: "1",
      title: "Attending",
    },
    {
      id: "2",
      title: "Failed",
    },
    {
      id: "3",
      title: "Cancelled",
    },
    {
      id: "4",
      title: "Completed",
    },
    {
      id: "7",
      title: "Upcoming",
    },
    {
      id: "9",
      title: "Waiting",
    },
  ],
};

export const VMSImageConstants: VMSImagesConstantTypes = {
  VETIC_CHAR_LOGO_WHITE: VeticCharWhiteLogo,
  NO_RECORD_OVERBOOKING_PERMISSION: NoRecordFoundOverbooking,
  BASIC: NoRecordFoundOverbooking,
  VETIC_LOGO: VeticLogo,
  NO_ADDRESS: NoAddress,
  NO_PWL_RESULTS: NoRecordPWL,
  NO_APPOINTMENTS: NoAppointmentsFound,
  EMR_RX_LOGO: EMRRXLogo,
  SCREEN_APPOINTMENT: BookingConfirmationScreen,
  SCREEN_SLOTS: SlotsScreen,
  SCREEN_CUSTOMER_ONBOARDING: CustomerOnboardingScreen,
  NO_CART: CartEmpty,
  SCREEN_PWL: PWLScreen,
  SCREEN_CUSTOMER_SEARCH: CustomerSearchScreen,
};

export const VMSThemeConstants: VMSThemeConstantsType = {
  buttons: {
    baseThemes: {
      solid: "border px-4 sm:px-6 py-2 rounded-lg bg-black text-white border-transparent text-[11px] whitespace-nowrap",
      secondary:
        "rounded bg-white  font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50",
      soft: "rounded  bg-indigo-50  font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100",
      primary: "bg-[#1B68D3] text-white hover:bg-[#1759B5]",
      success: "bg-[#FFF] text-[#1B68D3] border border-[#1B68D3] hover:bg-gray-200 ",
      "check-in": "bg-[#FFF] text-[#000] border border-[#E6E7EB] hover:bg-gray-300 ",
    },
    sizes: {
      SM: "px-2 py-1 text-[10px] sm:text-xs",
      MD: "px-2 py-1 text-[10px] sm:text-xs",
      LG: "px-2.5 py-1.5 text-[10px] sm:text-xs",
      XL: "px-3 py-2 text-[10px] sm:text-xs",
      "2XL": "px-3.5 py-1 sm:py-1.5 2xl:2.5 text-[10px] sm:text-xs",
      PWL: "min-w-[78px] px-[10px] py-[8px] text-[12px] rounded-[5px] font-semibold",
    },
  },
};

export const appointmentConfig = {
  waiting: {
    label: "Start Visit",
    type: "primary",
  },
  attending: {
    label: "Complete",
    type: "success",
  },
  upcoming: {
    label: "Check-in",
    type: "secondary",
  },
  "no show": {
    label: "Check-in",
    type: "secondary",
  },
  completed: {
    label: "",
    type: "",
  },
  cancelled: {
    label: "",
    type: "",
  },
};

export const atHomeUpdateStatusMap: Record<string, string> = {
  in_transit: "in_transit",
  waiting: "Waiting",
  attending: "attending",
  check_out: "Check Out",
};

export const atHomeAllowedUpdateStatuses = [
  "Waiting",
  "Auto Cancel",
  "Cancel",
  "Check Out",
  "attending",
  "Payment Due",
  "Pending",
  "Failed",
  "On Hold",
  "in_transit",
  "confirmed",
] as const;

export const LOCKED_CHILD_ITEM_IDS = [
  "c947364a-af15-4eec-861f-246d2198a4da",
  "532af538-1b09-418c-a440-9963bc478eec",
  "1636d7be-96d9-43c7-996a-4d7ebc7a67c2",
];

export const isMembershipExtensionCartItem = (tag?: string | null): boolean =>
  tag ===  "#mem_extension";

export const BLUE7_BORIVALI_CLINIC_ID = "adc0b1de-bae8-4b42-818c-d8d5486af130";

export const HOME_SERVICE_ID = "d32006d1-f450-4e75-82bb-947cfd9aac89";

export const VC_SERVICE_TYPE_IDS = [
  "42332401-7656-461e-81ee-7da6b889dafb",
  "11beb9c6-6e3f-41de-8d6a-d74de5eb3ab8",
];

export const VIDEO_CONSULTATION_FOLLOW_UP_SERVICE_IDS = [
  "caf76380-affe-469a-b8eb-1e0dca1f4013",
  "42332401-7656-461e-81ee-7da6b889dafb",
];

// Users allowed to urgent-cancel an invoice.
export const URGENT_CANCEL_ALLOWED_EMAILS = new Set([
  "raju.bhuyan@vetic.in",
  "kunal.khurana@vetic.in",
  "sirajul.mondal@vetic.in",
  "saurabh.singh@vetic.in",
  "rajesh.kumar@vetic.in",
  "abhishek.yadav@petpai.in",
]);

export const isUrgentCancelAllowedUser = (email: string | null | undefined): boolean =>
  URGENT_CANCEL_ALLOWED_EMAILS.has(email?.trim().toLowerCase() ?? "");

// Invoice statuses on which urgent cancel is offered. The status string comes
// straight from the invoice API, so it is normalised before the lookup.
export const URGENT_CANCEL_ELIGIBLE_STATUSES = ["paid", "partially_paid", "partially_returned", "partially_refunded"];

export const isUrgentCancelEligibleStatus = (status: string | null | undefined): boolean =>
  URGENT_CANCEL_ELIGIBLE_STATUSES.includes(status?.trim().toLowerCase() ?? "");

export const CLINIC_OPS_INVOICE_CANCEL_WINDOW_SECONDS = 30;
export const CENTRAL_OPS_INVOICE_CANCEL_WINDOW_SECONDS = 72000;
// A Snapmint-eligible invoice may wait on the customer's EMI checkout, so its default window is longer.
export const SNAPMINT_ELIGIBLE_CLINIC_OPS_INVOICE_CANCEL_WINDOW_SECONDS = 15 * 60;
export const SNAPMINT_ELIGIBLE_CENTRAL_OPS_INVOICE_CANCEL_WINDOW_SECONDS = 48 * 60 * 60;

export const getInvoiceCancelWindowSeconds = ({
  isUrgentCancelUser,
  isCentralOpps,
  isSnapmintEligible,
}: {
  isUrgentCancelUser: boolean;
  isCentralOpps: boolean;
  isSnapmintEligible?: boolean;
}): number | null => {
  if (isUrgentCancelUser) return null;
  if (isSnapmintEligible) {
    return isCentralOpps
      ? SNAPMINT_ELIGIBLE_CENTRAL_OPS_INVOICE_CANCEL_WINDOW_SECONDS
      : SNAPMINT_ELIGIBLE_CLINIC_OPS_INVOICE_CANCEL_WINDOW_SECONDS;
  }
  return isCentralOpps ? CENTRAL_OPS_INVOICE_CANCEL_WINDOW_SECONDS : CLINIC_OPS_INVOICE_CANCEL_WINDOW_SECONDS;
};

// Urgent cancel is offered for invoices created within the last 72 hours.
export const URGENT_CANCEL_WINDOW_HOURS = 72;

// `created_at` is an epoch timestamp in seconds (same as InvoiceCancelWindowGate).
export const isWithinUrgentCancelWindow = (created_at: number | null | undefined): boolean => {
  if (!created_at) return false;
  return differenceInHours(new Date(), fromUnixTime(created_at)) <= URGENT_CANCEL_WINDOW_HOURS;
};

// Cancel is offered on voucher_paid invoices created within the last 72 hours.
export const VOUCHER_PAID_CANCEL_WINDOW_HOURS = 72;

// `created_at` is an epoch timestamp in seconds (same as InvoiceCancelWindowGate).
export const isWithinVoucherPaidCancelWindow = (created_at: number | null | undefined): boolean => {
  if (!created_at) return false;
  return differenceInHours(new Date(), fromUnixTime(created_at)) <= VOUCHER_PAID_CANCEL_WINDOW_HOURS;
};

const VMSConstants: IVMSConstants = {
  string: VMSStringConstants,
  mapped: VMSMappedObjectConstants,
  array: VMSArrayConstants,
  images: VMSImageConstants,
  theme: VMSThemeConstants,
};

export const CITY_IMAGE_RULE = "Image should be .png format, 50 Px X 50 Px and 8 KB";

export const CITY_ERRORS = {
  PINCODE_REQUIRED: "Pincode is required",
  PINCODE_LENGTH: "Pincode should be of 6 digits",
  LAT_REQUIRED: "Latitude is required",
  LNG_REQUIRED: "Longitude is required",
  IMAGE_REQUIRED: "Image is required",
};

export const ERROR_MESSAGES = {
  SERVICE_REQUIRED: "Please select a service and it's sub-service",
  EQUIPMENT_NAME_REQUIRED: "Equipment name is required",
};

export const dayOfWeekOptions: ISelectMenuItemData[] = [
  { id: "All", title: "All" },
  { id: "Weekday", title: "Weekday" },
  { id: "Weekend", title: "Weekend" },
  { id: "Monday", title: "Monday" },
  { id: "Tuesday", title: "Tuesday" },
  { id: "Wednesday", title: "Wednesday" },
  { id: "Thursday", title: "Thursday" },
  { id: "Friday", title: "Friday" },
  { id: "Saturday", title: "Saturday" },
  { id: "Sunday", title: "Sunday" },
];

export const bookingFeeOptions: ISelectMenuItemData[] = [
  { id: "49", title: "49" },
  { id: "99", title: "99" },
  { id: "149", title: "149" },
  { id: "199", title: "199" },
  { id: "349", title: "349" },
];

export const advanceDaysOptions: ISelectMenuItemData[] = [
  { id: "0", title: "0 (D-Day / Same Day)" },
  { id: "1", title: "1 (1 Day Advance)" },
  { id: "2", title: "2 (2 Days Advance)" },
  { id: "3", title: "3 (3 Days Advance)" },
];

export const NO_PARAVET_ID = "9ed5dbce-a236-4d01-9791-db1396c52215";
export const NO_PARAVET_LABEL = "No Paravet";

export const BUTTON_BASE =
  "inline-flex justify-center items-center gap-2 h-9 px-4 sm:px-6 rounded-lg text-xs whitespace-nowrap transition-colors";
export const PRIMARY_BUTTON = `${BUTTON_BASE} bg-black text-white hover:bg-gray-800`;
export const SECONDARY_BUTTON = `${BUTTON_BASE} bg-[#F1F1F1] border border-gray-200 text-[#1F1F23] hover:bg-gray-200`;

export default VMSConstants;

// POS-CASH is an online payment mode the API returns for every clinic, but only
// these roles are allowed to collect against it. Everyone else — super_admin
// included — must not see the option at all.
export const POS_CASH_PAYMENT_MODE = "POS-CASH";

export const POS_CASH_ALLOWED_ROLES = new Set([
  "clinic_ops",
  "area_manager",
  "clinic_ops_doggyworld",
  "clinic_ops_bluecity",
  "clinic_ops_bluecity_po",
]);

export const isPosCashAllowedRole = (roleName: string | null | undefined): boolean =>
  POS_CASH_ALLOWED_ROLES.has(roleName?.trim().toLowerCase() ?? "");

// The API is not consistent about how it spells this mode ("POS-CASH", "POS_CASH",
// "POS Cash"), and useSelectMenuReducer rewrites underscores to spaces for the
// dropdown label — so match on the letters alone rather than an exact string.
export const isPosCashMode = (paymentMode: string | null | undefined): boolean =>
  (paymentMode ?? "").replace(/[^a-z0-9]/gi, "").toLowerCase() === "poscash";

// Packing alerts (navbar "orders pending packing" pill + live SSE stream) are
// shown only to these IAM roles: clinic_ops, clinic_ops_doggyworld.
export const PACKING_ALERTS_ROLE_IDS = new Set([
  "91e54ddc-7f42-461f-b2f5-9372eef9d000",
  "f73905b5-6919-43eb-a8dd-f38a3293bf9f",
]);

export const isPackingAlertsRole = (roleId: string | null | undefined): boolean =>
  PACKING_ALERTS_ROLE_IDS.has(roleId?.trim() ?? "");

// Window event the OMS page fires when the Confirmed tab is opened, so the
// header's packing-alerts hook can refresh its count (safety net, not polling).
export const PACKING_ALERTS_REFRESH_EVENT = "vetic:packing-alerts-refresh";
